import {type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import type {LexicalEditor} from "lexical";
import {
    $getSelection,
    $isRangeSelection,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_NORMAL,
    KEY_ESCAPE_COMMAND,
    mergeRegister,
    SELECTION_CHANGE_COMMAND,
} from "lexical";
import {Box, List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography} from "@mui/material";
import type {PromptSlotKind} from "../../utils/promptPlaceholders";
import {CustomPopper} from "../../../../components/customPopper/CustomPopper.tsx";
import {
    $createEntityPlaceholderNode,
    $replaceSingleEntityPlaceholderWithValues,
    $replaceSingleEntityWithSupplierSelections,
    isSupplierPickerSelectionArray,
} from "./entityPlaceholderNode.tsx";
import type {SupplierPickerSelection} from "../AgentTemplates/menus/SupplierTree.ts";
import {isSlashPickerKind, type SlashRootKind} from "./entityPickerMenuComponents.tsx";
import {
    $applyTextRangeReplace,
    $getCollapsedSlashQuery,
    $getSelectedSlashCommand,
    type TextRangeRef,
} from "./slashCommandLexical";

import SupplierIcon from "../../../../components/icons/svg/StoreIcon.svg?react";
import {EntityPlaceholderContext} from "./entityPlaceholderContext.tsx";
import {ENTITY_PICKER_MENU_COMPONENTS} from "./EntityPickerMenu.tsx";
// import CategoryIcon from "../../../../components/icons/svg/FlowChartIcon.svg?react";
import MaterialIcon from "../../../../components/icons/svg/ChartMagnifierIcon.svg?react";
// import PlantIcon from "../../../../components/icons/svg/MergeRightIcon.svg?react";
// import CostCenterIcon from "../../../../components/icons/svg/TrendDownGraphIcon.svg?react";
import LockIcon from "../../../../components/icons/svg/LockIcon.svg?react";

const ROOT_ITEMS: {kind: SlashRootKind; label: string; icon: ReactNode}[] = [
    {kind: "supplier", label: "Supplier", icon: <SupplierIcon/>},
    // { kind: "category", label: "Category", icon: <CategoryIcon /> },
    {kind: "material", label: "Material", icon: <MaterialIcon/>},
    // { kind: "plant", label: "Plant", icon: <PlantIcon /> },
    // { kind: "cost_center", label: "Cost center", icon: <CostCenterIcon /> },
];

function slotKindFromCommandWord(word: string): SlashRootKind | null {
    const w = word.toLowerCase().replace(/-/g, "_");
    const map: Record<string, SlashRootKind> = {
        supplier: "supplier",
        category: "category",
        material: "material",
        plant: "plant",
        cost_center: "cost_center",
    };
    return map[w] ?? null;
}

function labelForKind(kind: PromptSlotKind): string {
    switch (kind) {
        case "supplier":
            return "Supplier";
        case "category":
            return "Category";
        case "material":
            return "Material";
        case "plant":
            return "Plant";
        case "cost_center":
            return "Cost center";
        default:
            return "Item";
    }
}

function isRootItemSelectable(it: {label: string}): boolean {
    return it.label !== "Material";
}

function readCaretRect(editor: LexicalEditor): DOMRect | null {
    const rootEl = editor.getRootElement();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
        return null;
    }
    const anchorNode = sel.anchorNode;
    if (
        rootEl &&
        anchorNode &&
        !rootEl.contains(anchorNode) &&
        anchorNode !== rootEl
    ) {
        return null;
    }
    const range = sel.getRangeAt(0);
    const rects = range.getClientRects();
    if (rects.length > 0) {
        return rects[0]!;
    }
    const b = range.getBoundingClientRect();
    if (b.width === 0 && b.height === 0) {
        return null;
    }
    return b;
}

export type SlashCommandPluginProps = {
    disabled?: boolean;
};

export function SlashCommandPlugin({disabled}: SlashCommandPluginProps) {
    const {openPlacement} = useContext(EntityPlaceholderContext);
    const [editor] = useLexicalComposerContext();
    const [phase, setPhase] = useState<"closed" | "root" | "picker">("closed");
    const phaseRef = useRef(phase);
    const [pickerKind, setPickerKind] = useState<SlashRootKind | null>(null);

    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
    const [rootQuery, setRootQuery] = useState("");
    const [rootReplace, setRootReplace] = useState<TextRangeRef | null>(null);
    const [rootHighlightedIndex, setRootHighlightedIndex] = useState(0);
    const rootHighlightedIndexRef = useRef(0);
    const pickerKindRef = useRef<SlashRootKind>("supplier");

    const pickerRangeRef = useRef<TextRangeRef | null>(null);

    const closeAll = useCallback(() => {
        phaseRef.current = "closed";
        setPhase("closed");
        setAnchorRect(null);
        setRootReplace(null);
        setRootQuery("");
        setPickerKind(null);
        pickerRangeRef.current = null;
    }, []);

    const openPicker = useCallback(
        (kind: SlashRootKind, replace: TextRangeRef, rect: DOMRect | null) => {
            phaseRef.current = "picker";
            pickerKindRef.current = kind;
            pickerRangeRef.current = replace;
            setPickerKind(kind);
            setPhase("picker");
            let r = rect;
            if (!r) {
                const el = editor.getRootElement();
                if (el) {
                    const br = el.getBoundingClientRect();
                    r = new DOMRect(br.left + 8, br.top + 8, 2, 2);
                }
            }
            if (r) {
                setAnchorRect(r);
            }
        },
        [editor],
    );

    const applyValues = useCallback(
        (values: string[] | SupplierPickerSelection[]) => {
            const range = pickerRangeRef.current;
            const kind = pickerKindRef.current;
            if (!range) {
                return;
            }
            editor.update(() => {
                if (kind === "supplier" && isSupplierPickerSelectionArray(values)) {
                    $replaceSingleEntityWithSupplierSelections(range.anchorKey, values);
                } else {
                    const label = labelForKind(kind);
                    $replaceSingleEntityPlaceholderWithValues(
                        range.anchorKey,
                        label,
                        kind,
                        values as string[],
                    );
                }
            });
            closeAll();
            queueMicrotask(() => editor.focus());
        },
        [closeAll, editor],
    );

    useEffect(() => {
        phaseRef.current = phase;
    }, [phase]);

    useEffect(() => {
        if (!disabled) {
            return undefined;
        }
        const id = window.requestAnimationFrame(() => {
            closeAll();
        });
        return () => window.cancelAnimationFrame(id);
    }, [closeAll, disabled]);

    useEffect(() => {
        if (disabled) {
            return undefined;
        }

        return mergeRegister(
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    if (phaseRef.current === "picker") {
                        return false;
                    }
                    editor.getEditorState().read(() => {
                        const sel = $getSelection();
                        if (!$isRangeSelection(sel) || sel.isCollapsed()) {
                            return;
                        }
                        const selected = $getSelectedSlashCommand(sel);
                        if (!selected) {
                            return;
                        }
                        const kind = slotKindFromCommandWord(selected.word);
                        if (!kind) {
                            return;
                        }
                        queueMicrotask(() => {
                            if (phaseRef.current === "picker") {
                                return;
                            }
                            openPicker(kind, selected.range, readCaretRect(editor));
                        });
                    });
                    return false;
                },
                COMMAND_PRIORITY_NORMAL,
            ),

            editor.registerUpdateListener(({editorState}) => {
                if (phaseRef.current === "picker") {
                    return;
                }

                let nextPhase: "closed" | "root" = "closed";
                let nextReplace: TextRangeRef | null = null;
                let nextQuery = "";
                let skipSlashUi = false;

                editorState.read(() => {
                    const sel = $getSelection();
                    if (!$isRangeSelection(sel)) {
                        nextPhase = "closed";
                        return;
                    }

                    if (!sel.isCollapsed()) {
                        const picked = $getSelectedSlashCommand(sel);
                        if (picked && slotKindFromCommandWord(picked.word)) {
                            skipSlashUi = true;
                            return;
                        }
                    }
                    const slash = $getCollapsedSlashQuery(sel);
                    if (slash) {
                        nextPhase = "root";
                        nextReplace = slash.range;
                        nextQuery = slash.query;
                    } else {
                        nextPhase = "closed";
                    }
                });
                queueMicrotask(() => {
                    if (phaseRef.current === "picker") {
                        return;
                    }
                    if (skipSlashUi) {
                        return;
                    }
                    const rect = readCaretRect(editor);
                    if (nextPhase === "root") {
                        setPhase("root");
                        setRootReplace(nextReplace);
                        setRootQuery(nextQuery);
                        if (rect) {
                            setAnchorRect(rect);
                        }
                    } else {
                        setPhase("closed");
                        setRootReplace(null);
                        setRootQuery("");
                        setAnchorRect(null);
                    }
                });
            }),

            editor.registerCommand(
                KEY_ESCAPE_COMMAND,
                () => {
                    if (phaseRef.current !== "closed") {
                        closeAll();
                        return true;
                    }
                    return false;
                },
                COMMAND_PRIORITY_HIGH,
            ),
        );
    }, [closeAll, disabled, editor, openPicker]);

    const virtualAnchor = useMemo(() => {
        if (!anchorRect) {
            return null;
        }
        return {
            getBoundingClientRect: () => anchorRect,
        };
    }, [anchorRect]);

    const filteredRoot = useMemo(() => {
        const q = rootQuery.toLowerCase();
        return ROOT_ITEMS.filter(
            (items) =>
                !q ||
                items.label.toLowerCase().includes(q) ||
                items.kind.toLowerCase().includes(q),
        );
    }, [rootQuery]);

    const selectableRootItems = useMemo(
        () => filteredRoot.filter(isRootItemSelectable),
        [filteredRoot],
    );

    const highlightedRootKind = selectableRootItems[rootHighlightedIndex]?.kind;

    useEffect(() => {
        rootHighlightedIndexRef.current = 0;
        const frameId = window.requestAnimationFrame(() => {
            setRootHighlightedIndex(0);
        });
        return () => window.cancelAnimationFrame(frameId);
    }, [selectableRootItems]);

    const handleRootPick = useCallback(
        (kind: SlashRootKind) => {
            const range = rootReplace;
            editor.update(() => {
                const node = $createEntityPlaceholderNode(labelForKind(kind), kind, null);
                if (range) {
                    $applyTextRangeReplace(range, [node], true);
                    range.anchorKey = node.__key;
                }
            });
            if (!range) {
                return;
            }
            openPicker(kind, range, anchorRect);
        },
        [anchorRect, editor, openPicker, rootReplace],
    );

    const handleRootPickRef = useRef(handleRootPick);
    useEffect(() => {
        handleRootPickRef.current = handleRootPick;
    }, [handleRootPick]);

    useEffect(() => {
        if (phase !== "root" || selectableRootItems.length === 0) {
            return undefined;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            const items = selectableRootItems;
            if (items.length === 0) {
                return;
            }
            if (event.key === "ArrowDown") {
                event.preventDefault();
                event.stopPropagation();
                setRootHighlightedIndex((index) => {
                    const next = (index + 1) % items.length;
                    rootHighlightedIndexRef.current = next;
                    return next;
                });
                return;
            }
            if (event.key === "ArrowUp") {
                event.preventDefault();
                event.stopPropagation();
                setRootHighlightedIndex((index) => {
                    const next = (index - 1 + items.length) % items.length;
                    rootHighlightedIndexRef.current = next;
                    return next;
                });
                return;
            }
            if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                const item = items[rootHighlightedIndexRef.current];
                if (item) {
                    handleRootPickRef.current(item.kind);
                }
            }
        };
        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, [phase, selectableRootItems]);

    const openPopper = phase !== "closed" && virtualAnchor !== null;
    let pickerMenuEl: ReactNode = null;
    if (phase === "picker" && isSlashPickerKind(pickerKind)) {
        const PickerMenu = ENTITY_PICKER_MENU_COMPONENTS[pickerKind];
        pickerMenuEl = <PickerMenu key={pickerKind} onApply={applyValues}/>;
    }
    return (
        <CustomPopper
            open={openPopper}
            anchorEl={virtualAnchor as unknown as HTMLElement}
            onClose={closeAll}
            disableTransition
            paperSx={{py: 0.5, px: 0}}
            placement={openPlacement.placement}
            modifiers={[
                {
                    name: "offset",
                    options: {
                        offset: [0, 10],
                    },
                },
            ]}
        >
            {phase === "root" && (
                <Box sx={{minWidth: 320, maxHeight: 360, overflow: "auto", py: 0.5}}>
                    <List dense disablePadding sx={{p: 0}}>
                        {filteredRoot.map((it) => {
                            const isLocked = it.label === "Material";
                            return (
                                <ListItemButton
                                    key={it.kind}
                                    selected={!isLocked && it.kind === highlightedRootKind}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    {...(!isLocked && {
                                        onClick: () => handleRootPick(it.kind),
                                        onMouseEnter: () => {
                                            const index = selectableRootItems.findIndex(
                                                (item) => item.kind === it.kind,
                                            );
                                            if (index >= 0) {
                                                rootHighlightedIndexRef.current = index;
                                                setRootHighlightedIndex(index);
                                            }
                                        },
                                    })}
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        ...(isLocked && {
                                            "&.MuiListItemButton-root:hover": {
                                                backgroundColor: "transparent",
                                                cursor: "default",
                                            },
                                        }),
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: "37px",
                                            lineHeight: "170%",
                                            opacity: isLocked ? 0.5 : 1,
                                        }}
                                    >
                                        {it.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        sx={{py: 0}}
                                        slotProps={{
                                            secondary: {color: "#44474E", fontSize: '12px'},
                                        }}
                                        primary={
                                            <Stack direction="row" alignItems="center" gap="4px">
                                                <Stack>
                                                    <Typography
                                                        variant="body1"
                                                        color={isLocked ? "#44474E" : "#4088F8"}
                                                        sx={{mb: 0.5, opacity: isLocked ? 0.5 : 1}}
                                                    >{`/${it.label}`}</Typography>

                                                </Stack>
                                            </Stack>
                                        }
                                        secondary={
                                            isLocked
                                                ? "Upgrade to access this entity"
                                                : `Insert one or more ${it.label}`
                                        }
                                    />
                                    {isLocked && <LockIcon style={{marginLeft: "auto"}}/>}

                                </ListItemButton>
                            );
                        })}
                        {filteredRoot.length === 0 && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{px: 2, py: 1}}
                            >
                                No matches
                            </Typography>
                        )}
                    </List>
                </Box>
            )}
            {pickerMenuEl}
        </CustomPopper>
    );
}
